/**
 * Single source of truth for the browser-side "fake user mode" flag.
 *
 * When an admin previews the app as a normal user, this flag is persisted to
 * localStorage under FAKE_USER_STORAGE_KEY. The api-client mirrors it onto every
 * request as the FAKE_USER_HEADER (see api-client.ts), and the gears-api
 * authentication middleware then downgrades that request to a non-admin view. It
 * never touches the stored/session admin flag, so switching back is immediate.
 *
 * All access to the flag goes through this module so the storage key, the 'true'
 * encoding, and the private-mode/quota try/catch guard live in exactly one place.
 * Every function is browser-only: it no-ops (or returns false) when window /
 * localStorage is unavailable, so the server-side api-client never sends the
 * header.
 */
export const FAKE_USER_STORAGE_KEY = 'racemap:fakeUser';

export function isFakeUserMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FAKE_USER_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function activateFakeUserMode(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FAKE_USER_STORAGE_KEY, 'true');
  } catch {
    // ignore — localStorage may be unavailable (private mode/quota)
  }
}

export function deactivateFakeUserMode(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(FAKE_USER_STORAGE_KEY);
  } catch {
    // ignore — localStorage may be unavailable (private mode/quota)
  }
}

/** Flips the persisted flag and returns the new state (for choosing UI feedback). */
export function toggleFakeUserMode(): boolean {
  const next = !isFakeUserMode();
  if (next) {
    activateFakeUserMode();
  } else {
    deactivateFakeUserMode();
  }
  return next;
}
